import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { TagEditorComponent } from '@tourfold/libraries/shared-ui';
import type { DocumentResponse, FolderResponse } from '@tourfold/libraries/tf-api-client-public-v2';
import type { SmartListKind } from './folder-browser.component';
import { FoldersService } from '../services/folders.service';
import { DocumentsService, type BinaryUploadProgress, uploadBinary } from '../services/documents.service';
import { parseProblemFromError } from '../services/extract-problem-message';
import { translateProblem } from '../services/translate-problem';
import {
  FolderOrDocumentNameValidationError,
  folderOrDocumentNameErrorKey,
  requireValidFolderOrDocumentName,
} from '../services/folder-or-document-name';
import { mimeColorClass } from '../detail-pane/mime-color';
import {
  type DocumentMgmtDragPayload,
  dataTransferHasType,
  isInternalDrag,
  readDragPayload,
  writeDragPayload,
} from './drag-payload';
import { DocumentMgmtDragService } from './document-mgmt-drag.service';
import { DOCUMENT_MANAGEMENT_CAPABILITIES } from '../services/document-management-capabilities';
import {
  DEFAULT_FOLDER_SORT,
  type FolderSortKey,
} from '../services/document-management-preferences.service';
import {
  dragUploadEntries,
  flatFileEntries,
  folderSegments,
  isPackageDirectoryName,
  nextAvailableFileName,
  normalizeLookupName,
  shouldConfirmLargeFolderUpload,
  type UploadBatchEntry,
  type UploadDropStats,
  type UploadFailureEntry,
  type UploadFileEntry,
  ZIP_MIME_TYPE,
} from './folder-upload-entries';

/**
 * One pending upload in this column. Rendered as a ghost row at the top of the documents
 * section so the user sees their file appear immediately and watches it move from "queued" to
 * "complete" before the batch clears after the last upload.
 */
type PendingUploadPhase = 'preparing' | 'queued' | 'uploading' | 'finalizing' | 'complete';

interface PendingUpload {
  readonly id: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly phase: PendingUploadPhase;
  readonly progress: number | null;
}

interface UploadFailure {
  readonly id: string;
  readonly fileName: string;
  readonly message: string;
}

interface DocumentNameConflict {
  readonly name: string;
  readonly document: DocumentResponse | null;
}

interface ConflictDialogState {
  readonly id: string;
  readonly displayPath: string;
  readonly uploadName: string;
  readonly existingName: string;
  readonly canUploadNewVersion: boolean;
  readonly newVersionDisabledReason: string | null;
}

type ConflictDecision =
  | { readonly kind: 'rename'; readonly name: string }
  | { readonly kind: 'version' }
  | { readonly kind: 'skip' };

/**
 * One column of the Miller-column folder browser. Loads folders + documents under `parentId`
 * (`null` = tenant root, where the API only allows folders). Selecting a folder drills in;
 * selecting a document highlights it for the detail pane. Header carries +folder, upload, and
 * count. Body supports drag-and-drop to upload to this column's folder.
 *
 * `ViewEncapsulation.None` so descendant selectors can target PrimeNG-rendered internals
 * (e.g. `.folder-column__new-folder-btn .p-button-icon`). All selectors in the SCSS file are
 * uniquely prefixed with `folder-column*` to keep global leakage contained.
 */
@Component({
  selector: 'tf-folder-column',
  imports: [
    TranslateModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    TagEditorComponent,
  ],
  templateUrl: './folder-column.component.html',
  styleUrl: './folder-column.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FolderColumnComponent {
  private readonly foldersService = inject(FoldersService);
  private readonly documentsService = inject(DocumentsService);
  private readonly translate = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dragService = inject(DocumentMgmtDragService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly capabilities = inject(DOCUMENT_MANAGEMENT_CAPABILITIES);

  readonly parentId = input<string | null>(null);
  /**
   * Kind of this column's own folder. `null` for the tenant root (no own folder) or before a
   * scoped root's kind resolves. `managed_branch` folders (`/objects`, `/objects/<type>`) are
   * structural scaffolding owned by the system — the backend rejects uploads (422
   * `managed_branch_not_a_content_host`) and folder creation (403 `cannot_create_under_managed_branch`)
   * there for every user, so this column disables those affordances rather than letting the request
   * fail. `user` / `managed` folders are valid content hosts.
   */
  readonly folderKind = input<FolderResponse['kind'] | null>(null);
  readonly selectedChildId = input<string | null>(null);
  readonly selectedDocumentId = input<string | null>(null);
  readonly busy = input(false);
  /** Bump from the parent to force a reload (e.g. after a sibling create or upload finalize). */
  readonly reloadKey = input(0);
  /** Header label — the parent folder's display name (or a fallback for the root column). */
  readonly headerLabel = input<string>('');
  /**
   * When true, render two extra rows ("Recent" and "Starred") at the top of the list. Reserved
   * for the column-0 tenant-root view; no-op on every other column.
   */
  readonly showSmartEntries = input(false);
  /** Currently-selected smart entry (drives row highlighting). */
  readonly selectedSmart = input<SmartListKind | null>(null);
  /**
   * Active sort preset, owned by the parent browser via user preferences. Folders are always
   * shown first and sorted by name (matching the browser's `sortFoldersManagedFirst` policy);
   * only the document section honours this — `name`/`size`/`uploaded` map to the documents
   * endpoint's `name`/`size`/`updatedAt` sort columns respectively. The backend whitelist guards
   * unknown values so a stale preference won't crash the column.
   */
  readonly sortKey = input<FolderSortKey>(DEFAULT_FOLDER_SORT);
  /**
   * When true, render display-only tag chips below each folder/document name. Owned by the
   * parent browser via user preferences and applied uniformly across all columns.
   */
  readonly showTags = input(false);

  readonly folderSelected = output<FolderResponse>();
  readonly documentSelected = output<DocumentResponse>();
  readonly newFolderRequested = output<string | null>();
  readonly smartSelected = output<SmartListKind>();
  /**
   * Fires after a successful move triggered from this column. The parent browser uses it to
   * refresh affected columns (the source one this row was just removed from, and the target one
   * if it's currently open).
   */
  readonly moveCompleted = output<{ readonly sourceParentId: string | null; readonly targetParentId: string | null }>();
  /**
   * Fires when the user clicks empty space inside the column body (i.e. not on a row, banner, or
   * action button). The browser uses it to deselect any active document and surface the column's
   * own folder in the detail pane — clicking outside the rows returns the user to the folder view.
   */
  readonly bodyBackgroundClicked = output<void>();
  /**
   * Fires when keyboard navigation walks past this column's edge. The parent browser then focuses
   * the previous / next column's selected (or first) row. Includes:
   * - Plain Left arrow on any row in this column.
   * - Plain Right arrow on a row that doesn't have a child column to drill into (e.g. document rows).
   * - Plain Right arrow on a folder/smart row AFTER the row was activated — the browser waits for
   *   the freshly-opened column to render and then focuses its first row.
   * - Ctrl+Left / Ctrl+Right (shuttle without activation).
   */
  readonly focusOutOfBounds = output<'previous' | 'next'>();

  protected readonly folders = signal<FolderResponse[]>([]);
  protected readonly documents = signal<DocumentResponse[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly hasError = signal(false);
  protected readonly pendingUploads = signal<readonly PendingUpload[]>([]);
  protected readonly uploadFailures = signal<readonly UploadFailure[]>([]);
  /**
   * Active drag-over state, drives the body overlay's icon + copy:
   *   - `'upload'`: OS file/folder drop on a non-root column → upload into this folder.
   *   - `'move-to-root'`: internal folder row dragged onto the root column body → promotes to root.
   *   - `'os-folder-create-root'`: OS drop on the root column → walk top-level directories and
   *     create matching folders (loose files get rejected at drop time, since root has no docs).
   */
  protected readonly dragOverMode = signal<'upload' | 'move-to-root' | 'os-folder-create-root' | null>(null);
  protected readonly isDragOver = computed(() => this.dragOverMode() !== null);
  protected readonly conflictDialog = signal<ConflictDialogState | null>(null);
  protected readonly conflictRenameValue = signal('');
  protected readonly conflictRenameError = signal<string | null>(null);
  /** Id of the row currently being dragged (this column's view). Cleared on dragend. */
  protected readonly draggingRowId = signal<string | null>(null);
  /** Id of the folder row currently highlighted as a valid drop target. */
  protected readonly dropHoverFolderId = signal<string | null>(null);

  protected readonly hasFolders = computed(() => this.folders().length > 0);
  protected readonly hasDocuments = computed(() => this.documents().length > 0);
  protected readonly hasPending = computed(() => this.pendingUploads().length > 0);
  protected readonly hasFailures = computed(() => this.uploadFailures().length > 0);
  protected readonly itemCount = computed(() => this.folders().length + this.documents().length);
  protected readonly hasAnyContent = computed(
    () => this.hasFolders() || this.hasDocuments() || this.hasPending() || this.hasFailures(),
  );
  /** True for a real folder column; `false` for the synthetic tenant-root column (`parentId === null`). */
  protected readonly isFolderColumn = computed(() => this.parentId() !== null);

  /**
   * `managed_branch` folders are structural — they host no content. Both file upload and folder
   * creation are rejected there by the backend for every user, irrespective of grants.
   */
  protected readonly isStructuralFolder = computed(() => this.folderKind() === 'managed_branch');

  /** Whether the signed-in user holds `documents:create` (host-provided; defaults true in isolation). */
  protected readonly canCreateDocuments = computed(() => this.capabilities.canCreateDocuments());

  /**
   * File upload is possible iff this is a real, content-hosting folder AND the user can create
   * documents. Drives the drop overlay, the `u` shortcut, the drop hint, and the upload button's
   * enabled state. The tenant root (`parentId === null`) doesn't support direct file uploads — it
   * only accepts dropped *directories* via the separate OS-folder-create path.
   */
  protected readonly canUpload = computed(
    () => this.isFolderColumn() && !this.isStructuralFolder() && this.canCreateDocuments(),
  );

  /**
   * Folder creation is possible in `user` / `managed` folders and at the tenant root, but never
   * under a `managed_branch`, and only with `documents:create`. Drives the `+` shortcut, the
   * root OS-folder-create drop, and the "New folder" button's enabled state.
   */
  protected readonly canCreateFolder = computed(
    () => !this.isStructuralFolder() && this.canCreateDocuments(),
  );

  /**
   * Tooltip for the upload button. Shows the plain action label when enabled, or — when disabled —
   * the reason: a generic "no permission" message (missing `documents:create`) or a structural
   * "can't upload into a system folder" message (`managed_branch`). Permission takes precedence
   * because it blocks every folder, not just this one.
   */
  protected readonly uploadTooltipKey = computed(() => {
    if (this.canUpload()) return 'DOCUMENT_MANAGEMENT.UPLOAD';
    if (!this.canCreateDocuments()) return 'GENERAL.NO_PERMISSION';
    return 'DOCUMENT_MANAGEMENT.UPLOAD_NOT_ALLOWED_HERE';
  });

  /** Tooltip for the "New folder" button, mirroring {@link uploadTooltipKey}. */
  protected readonly newFolderTooltipKey = computed(() => {
    if (this.canCreateFolder()) return 'DOCUMENT_MANAGEMENT.NEW_FOLDER';
    if (!this.canCreateDocuments()) return 'GENERAL.NO_PERMISSION';
    return 'DOCUMENT_MANAGEMENT.NEW_FOLDER_NOT_ALLOWED_HERE';
  });

  /** Hidden file input — the upload button programmatically clicks this. */
  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  /** Rename input inside the conflict dialog; auto-focused once the dialog renders. */
  protected readonly conflictInput = viewChild<ElementRef<HTMLInputElement>>('conflictInput');

  /**
   * Tracks the parentId from the previous effect run so we can distinguish "parent changed"
   * (clear stale rows + show spinner — a fresh column) from "reloadKey bumped" (keep showing
   * the existing rows during the silent re-fetch — stale-while-revalidate).
   */
  private lastLoadedParentId: string | null | undefined = undefined;
  private conflictDialogResolve: ((decision: ConflictDecision) => void) | null = null;
  private readonly pendingUploadClearTimers = new Set<ReturnType<typeof setTimeout>>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      for (const timer of this.pendingUploadClearTimers) clearTimeout(timer);
      this.pendingUploadClearTimers.clear();
    });

    effect(() => {
      const parent = this.parentId();
      this.reloadKey(); // depend on it so caller-driven reloads re-fire load()
      this.sortKey(); // re-fetch when the active sort preset changes
      const isParentChange = parent !== this.lastLoadedParentId;
      this.lastLoadedParentId = parent;
      if (isParentChange) {
        this.folders.set([]);
        this.documents.set([]);
        // Pending uploads / failures are scoped to a column's parentId; clear them on switch.
        this.pendingUploads.set([]);
        this.uploadFailures.set([]);
      }
      this.load(parent);
    });

    // Auto-focus the conflict-rename input once the conflict dialog opens. PrimeNG's dialog
    // teleports its body via overlay so the input is only in the DOM after the next render tick.
    effect(() => {
      if (this.conflictDialog() === null) return;
      afterNextRender(
        () => this.conflictInput()?.nativeElement.focus(),
        { injector: this.injector },
      );
    });
  }

  protected onSelectFolder(folder: FolderResponse): void {
    if (this.busy()) return;
    this.folderSelected.emit(folder);
  }

  protected onSelectFolderKeydown(event: KeyboardEvent, folder: FolderResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectFolder(folder);
    }
  }

  protected onSelectDocument(doc: DocumentResponse): void {
    if (this.busy()) return;
    this.documentSelected.emit(doc);
  }

  protected onSelectDocumentKeydown(event: KeyboardEvent, doc: DocumentResponse): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectDocument(doc);
    }
  }

  /**
   * Column-level keyboard navigation. Captures arrow keys at the host so all rows share one
   * handler — Enter / Space are still served by the per-row keydown bindings.
   *
   * - Up / Down: walk to the previous / next focusable row in this column. Does not wrap.
   * - Home / End: jump to the first / last row.
   * - Plain Right on a folder/smart row activates it (drills in or opens the smart list); the
   *   parent waits for the freshly-opened column to render and focuses its first row. On a
   *   document row Right is a no-op for navigation (document rows are leaves).
   * - Ctrl+Right: shuttle to the next *already-open* column without activating.
   * - Plain Left or Ctrl+Left: shuttle to the previous column.
   *
   * Rows are queried via `[role="option"]` which matches the listbox semantics already on the
   * smart entries / folders / documents and skips pending-upload rows + dividers + failure
   * banners (none of which carry the role).
   */
  @HostListener('keydown', ['$event'])
  protected onHostKeydown(event: KeyboardEvent): void {
    // Only react when focus is on a row inside this column. Arrow keys inside text inputs,
    // dialogs, etc. are unrelated.
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;
    if (!active.matches('[role="option"]')) return;
    if (!this.hostRef.nativeElement.contains(active)) return;

    // Column-level action shortcuts (`+` = new folder, `u` = upload) mirror the header buttons.
    // Handled in a helper so this navigation handler stays under the cognitive-complexity budget.
    if (this.handleActionShortcutKey(event)) return;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        this.focusRowSibling(active, +1);
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        this.focusRowSibling(active, -1);
        return;
      }
      case 'Home': {
        event.preventDefault();
        this.focusFirstRow();
        return;
      }
      case 'End': {
        event.preventDefault();
        this.rowElements().at(-1)?.focus();
        return;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        this.focusOutOfBounds.emit('previous');
        return;
      }
      case 'ArrowRight': {
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          this.focusOutOfBounds.emit('next');
          return;
        }
        // Plain Right: activate the focused row when it can drill (folder / smart entry). We
        // dispatch a synthetic click rather than re-deriving which folder/smart kind the row
        // represents — the existing click bindings already handle that. The parent then walks
        // down the new column once it has rendered.
        if (active.matches('.folder-column__row--folder, .folder-column__row--smart')) {
          active.click();
          this.focusOutOfBounds.emit('next');
          return;
        }
        // Document rows are leaves — Right is a no-op.
        return;
      }
      default:
        return;
    }
  }

  /**
   * Handles the column action-shortcut keys (`+` = new folder, `u`/`U` = upload). Returns `true`
   * when the key is one of those (so the caller stops processing), `false` otherwise. Modifier
   * chords (Ctrl/Meta/Alt) are left untouched so app-level shortcuts keep their meaning, and the
   * action only fires when it's currently allowed (`canCreateFolder` / `canUpload`).
   */
  private handleActionShortcutKey(event: KeyboardEvent): boolean {
    const key = event.key;
    if (key === '+') {
      if (!event.ctrlKey && !event.metaKey && !event.altKey && this.canCreateFolder()) {
        event.preventDefault();
        this.onNewFolder();
      }
      return true;
    }
    if (key === 'u' || key === 'U') {
      if (!event.ctrlKey && !event.metaKey && !event.altKey && this.canUpload()) {
        event.preventDefault();
        this.onUploadClick();
      }
      return true;
    }
    return false;
  }

  /** All focusable rows in this column, in DOM order. */
  private rowElements(): readonly HTMLElement[] {
    return Array.from(
      this.hostRef.nativeElement.querySelectorAll<HTMLElement>('[role="option"]'),
    );
  }

  private focusRowSibling(current: HTMLElement, delta: -1 | 1): void {
    const rows = this.rowElements();
    const idx = rows.indexOf(current);
    if (idx < 0) return;
    const next = rows[idx + delta];
    next?.focus();
  }

  /** Public — used by the parent browser when keyboard navigation enters this column. */
  focusFirstRow(): void {
    this.rowElements()[0]?.focus();
  }

  /**
   * Public — used by the parent when keyboard navigation re-enters this column from the right
   * (Left arrow on the next column). Lands on the row the user previously selected so the
   * cursor reflects the trail; falls back to the first row when nothing is selected.
   */
  focusSelectedRow(): void {
    const host = this.hostRef.nativeElement;
    const selected = host.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
    (selected ?? this.rowElements()[0])?.focus();
  }

  /**
   * Clicks anywhere in the column bubble up to the host. We only emit the "empty space" signal
   * when the click didn't land on a row, banner, or the column header — clicks on those already
   * have their own handlers and the browser would otherwise immediately undo a fresh selection.
   * Bound on the host (rather than on the body div in the template) so the lint rule about
   * focusable interactive elements doesn't flag a non-focusable wrapper.
   */
  @HostListener('click', ['$event'])
  protected onHostClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.folder-column__row, .folder-column__failure, .folder-column__header')) return;
    this.bodyBackgroundClicked.emit();
  }

  /** Keyboard equivalent — Escape from any focused row inside the column also drops the document. */
  @HostListener('keydown.escape')
  protected onHostEscape(): void {
    this.bodyBackgroundClicked.emit();
  }

  protected onNewFolder(): void {
    if (!this.canCreateFolder()) return;
    this.newFolderRequested.emit(this.parentId());
  }

  protected onUploadClick(): void {
    if (!this.canUpload()) return;
    this.fileInput()?.nativeElement.click();
  }

  protected onFileInputChange(event: Event): void {
    const fileInputElement = event.target as HTMLInputElement;
    if (fileInputElement.files && fileInputElement.files.length > 0) {
      void this.uploadFiles(Array.from(fileInputElement.files));
    }
    fileInputElement.value = ''; // allow re-uploading the same file
  }

  protected onDragOver(event: DragEvent): void {
    if (isInternalDrag(event.dataTransfer)) {
      // Per-folder-row drop targets handle internal moves themselves; we only step in for the
      // root column's body so a user can promote a folder to live at the tenant root.
      if (this.canAcceptInternalRootDrop()) {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        this.dragOverMode.set('move-to-root');
      }
      return;
    }
    const dataTransfer = event.dataTransfer;
    if (!dataTransferHasType(dataTransfer, 'Files')) return;
    if (this.canUpload()) {
      event.preventDefault();
      if (dataTransfer) dataTransfer.dropEffect = 'copy';
      this.dragOverMode.set('upload');
      return;
    }
    // Root column accepts OS drops too, but only when the payload contains directories (and the
    // user can create folders). We can't read the directory shape during dragover
    // (`webkitGetAsEntry()` is gated to drop), so accept the hover state here and reject loose-file
    // payloads at drop time.
    if (this.parentId() === null && this.canCreateFolder()) {
      event.preventDefault();
      if (dataTransfer) dataTransfer.dropEffect = 'copy';
      this.dragOverMode.set('os-folder-create-root');
    }
  }

  protected onDragLeave(event: DragEvent): void {
    if (this.didDragLeaveCurrentTarget(event)) this.dragOverMode.set(null);
  }

  protected onDrop(event: DragEvent): void {
    if (isInternalDrag(event.dataTransfer)) {
      if (!this.canAcceptInternalRootDrop()) return;
      event.preventDefault();
      event.stopPropagation();
      this.dragOverMode.set(null);
      const payload = readDragPayload(event.dataTransfer) ?? this.dragService.activeDrag();
      this.dragService.clear();
      this.draggingRowId.set(null);
      if (payload?.kind !== 'folder') return;
      this.confirmRootMove(payload);
      return;
    }
    this.dragOverMode.set(null);
    if (this.canUpload()) {
      event.preventDefault();
      void this.uploadDroppedItems(event.dataTransfer);
      return;
    }
    if (this.parentId() === null && this.canCreateFolder()) {
      event.preventDefault();
      void this.uploadDroppedItemsAtRoot(event.dataTransfer);
    }
  }

  /**
   * Eligibility for the root-column body acting as a "move to root" drop target. Reads the
   * payload from the singleton drag service since `dataTransfer.getData()` is hidden during
   * dragover for security.
   */
  private canAcceptInternalRootDrop(): boolean {
    if (this.parentId() !== null) return false;
    const payload = this.dragService.activeDrag();
    if (payload?.kind !== 'folder') return false;
    if (payload.sourceParentId === null) return false; // already at root
    return true;
  }

  private confirmRootMove(payload: DocumentMgmtDragPayload): void {
    const targetLabel = this.headerLabel()
      || this.translate.instant('DOCUMENT_MANAGEMENT.ROOT_LABEL');
    this.confirmationService.confirm({
      header: this.translate.instant('DOCUMENT_MANAGEMENT.MOVE_CONFIRM_TITLE'),
      message: this.translate.instant('DOCUMENT_MANAGEMENT.MOVE_CONFIRM_FOLDER', {
        name: payload.name,
        target: targetLabel,
      }),
      acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.MOVE_CONFIRM_ACCEPT'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        void this.performRootMove(payload);
      },
    });
  }

  private async performRootMove(payload: DocumentMgmtDragPayload): Promise<void> {
    try {
      await this.foldersService.moveToRoot(payload.id);
      this.moveCompleted.emit({
        sourceParentId: payload.sourceParentId,
        targetParentId: null,
      });
      this.reload();
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.MOVE_FAILED',
      );
      this.uploadFailures.update((list) => [
        ...list,
        { id: `move-failed-${Date.now()}`, fileName: payload.name, message },
      ]);
    }
  }

  /**
   * OS drop on the root column. Walks the top-level entries; only directory entries are
   * accepted (root has no documents, so loose files get rejected with an inline banner).
   */
  private async uploadDroppedItemsAtRoot(dataTransfer: DataTransfer | null): Promise<void> {
    if (!dataTransfer) return;
    const fallbackFiles = this.snapshotFiles(dataTransfer);
    const result = await dragUploadEntries(dataTransfer, {
      onArchivePreparing: (event) => this.addPreparingArchivePendingRow(event.displayPath),
    });
    if (!result || result.entries.length === 0) {
      if (fallbackFiles.length > 0) {
        this.surfaceRootLooseFilesError();
        return;
      }
      this.surfaceRootLooseFilesError();
      return;
    }
    const topLevelLooseEntries = result.entries.filter(
      (entry) =>
        (entry.kind === 'file' || entry.kind === 'failure') && !entry.folderPath,
    );
    if (topLevelLooseEntries.length > 0) {
      this.surfaceRootLooseFilesError();
      this.clearPendingUploadsForEntries(topLevelLooseEntries);
    }
    const folderScopedEntries = result.entries.filter(
      (entry) =>
        entry.kind === 'directory'
        || ((entry.kind === 'file' || entry.kind === 'failure') && !!entry.folderPath),
    );
    if (folderScopedEntries.length === 0) return;
    if (!await this.confirmLargeFolderUploadIfNeeded(this.statsForEntries(folderScopedEntries))) {
      this.clearPendingUploadsForEntries(folderScopedEntries);
      return;
    }
    await this.uploadEntries(folderScopedEntries);
  }

  private surfaceRootLooseFilesError(): void {
    const message = this.translate.instant('DOCUMENT_MANAGEMENT.ROOT_REJECTS_LOOSE_FILES');
    this.uploadFailures.update((list) => [
      ...list,
      { id: `root-rejects-${Date.now()}`, fileName: '', message },
    ]);
  }

  protected dismissFailure(id: string): void {
    this.uploadFailures.update((list) => list.filter((f) => f.id !== id));
  }

  protected onConflictRenameInput(event: Event): void {
    this.conflictRenameValue.set((event.target as HTMLInputElement).value);
    this.conflictRenameError.set(null);
  }

  protected onConflictRename(): void {
    let name: string;
    try {
      name = requireValidFolderOrDocumentName(this.conflictRenameValue());
    } catch (err) {
      this.conflictRenameError.set(
        this.localFolderOrDocumentNameErrorMessage(err)
          ?? this.translate.instant('DOCUMENT_MANAGEMENT.CONFLICT_RENAME_REQUIRED'),
      );
      return;
    }
    this.resolveConflictDialog({ kind: 'rename', name });
  }

  protected onConflictNewVersion(): void {
    if (!this.conflictDialog()?.canUploadNewVersion) return;
    this.resolveConflictDialog({ kind: 'version' });
  }

  protected onConflictSkip(): void {
    this.resolveConflictDialog({ kind: 'skip' });
  }

  protected onConflictDialogVisibilityChange(visible: boolean): void {
    if (!visible && this.conflictDialog()) {
      this.resolveConflictDialog({ kind: 'skip' });
    }
  }

  // ── Row drag (internal move) ────────────────────────────────────────

  /**
   * Document rows are always draggable. Folder rows are draggable only when `kind = 'user'`;
   * managed (`managed`, `managed_branch`) folders are pinned by their owning feature and the
   * backend rejects moves anyway.
   */
  protected isFolderDraggable(folder: FolderResponse): boolean {
    return folder.kind === 'user';
  }

  protected onDocumentRowDragStart(event: DragEvent, doc: DocumentResponse): void {
    if (!event.dataTransfer || !doc.id) return;
    if (this.isIncomplete(doc)) {
      event.preventDefault();
      return;
    }
    const payload: DocumentMgmtDragPayload = {
      kind: 'document',
      id: doc.id,
      sourceParentId: doc.folderId ?? this.parentId() ?? null,
      sourcePath: null,
      name: doc.name ?? '',
    };
    writeDragPayload(event.dataTransfer, payload);
    // Mirror to the singleton service so cross-column dragover handlers can read the payload —
    // `dataTransfer.getData()` is intentionally hidden during dragover for security.
    this.dragService.start(payload);
    this.draggingRowId.set(doc.id);
  }

  protected onFolderRowDragStart(event: DragEvent, folder: FolderResponse): void {
    if (!event.dataTransfer || !folder.id) return;
    if (!this.isFolderDraggable(folder)) {
      event.preventDefault();
      return;
    }
    const payload: DocumentMgmtDragPayload = {
      kind: 'folder',
      id: folder.id,
      sourceParentId: folder.parentId ?? this.parentId() ?? null,
      // The public-v2 `FolderResponse` doesn't expose the materialised path; descendant-cycle
      // prevention happens server-side and surfaces as `move_into_descendant`.
      sourcePath: null,
      name: folder.displayName ?? folder.name ?? '',
    };
    writeDragPayload(event.dataTransfer, payload);
    this.dragService.start(payload);
    this.draggingRowId.set(folder.id);
  }

  protected onRowDragEnd(): void {
    this.draggingRowId.set(null);
    this.dropHoverFolderId.set(null);
    this.dragService.clear();
  }

  /**
   * Folder rows are drop targets for an internal move. Eligible iff the target's `kind` is
   * `user` or `managed` and the source isn't trying to land on itself / one of its descendants.
   * Reads the payload from the singleton drag service rather than `dataTransfer` because the
   * latter's `getData()` is hidden during dragover for security.
   */
  protected onFolderRowDragOver(event: DragEvent, folder: FolderResponse): void {
    if (!isInternalDrag(event.dataTransfer)) return;
    const payload = this.dragService.activeDrag();
    if (!payload) return;
    if (!this.isValidDropTarget(folder, payload)) return;
    event.preventDefault();
    event.stopPropagation(); // don't bubble to the column-body OS-file overlay
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    if (folder.id) this.dropHoverFolderId.set(folder.id);
  }

  protected onFolderRowDragLeave(event: DragEvent, folder: FolderResponse): void {
    // Only clear the highlight when the pointer actually leaves the row (not when it hovers a
    // child element — those generate dragleave/dragenter pairs we don't want to react to).
    if (folder.id === this.dropHoverFolderId() && this.didDragLeaveCurrentTarget(event)) {
      this.dropHoverFolderId.set(null);
    }
  }

  protected onFolderRowDrop(event: DragEvent, folder: FolderResponse): void {
    if (!isInternalDrag(event.dataTransfer)) return;
    // Prefer the dataTransfer payload (the canonical channel during drop), fall back to the
    // service if the browser hands back an empty string for some reason.
    const payload = readDragPayload(event.dataTransfer) ?? this.dragService.activeDrag();
    if (!payload || !folder.id) return;
    event.preventDefault();
    event.stopPropagation();
    this.dropHoverFolderId.set(null);
    this.draggingRowId.set(null);
    this.dragService.clear();
    if (!this.isValidDropTarget(folder, payload)) return;

    const targetName = folder.displayName ?? folder.name ?? '';
    const messageKey = payload.kind === 'document'
      ? 'DOCUMENT_MANAGEMENT.MOVE_CONFIRM_DOCUMENT'
      : 'DOCUMENT_MANAGEMENT.MOVE_CONFIRM_FOLDER';

    this.confirmationService.confirm({
      header: this.translate.instant('DOCUMENT_MANAGEMENT.MOVE_CONFIRM_TITLE'),
      message: this.translate.instant(messageKey, { name: payload.name, target: targetName }),
      acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.MOVE_CONFIRM_ACCEPT'),
      rejectLabel: this.translate.instant('GENERAL.CANCEL'),
      // Make the cancel a quiet outlined button so the affirmative action reads as primary.
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => void this.performMove(payload, folder),
    });
  }


  private isValidDropTarget(target: FolderResponse, payload: DocumentMgmtDragPayload): boolean {
    if (!target.id) return false;
    if (target.kind !== 'user' && target.kind !== 'managed') return false;
    // Folders are never soft-deleted in the new model — no soft-delete-state guard needed here.
    if (payload.id === target.id) return false; // can't drop folder onto itself
    if (payload.sourceParentId === target.id) return false; // already lives there
    // Folder-into-descendant cycle prevention happens server-side (`move_into_descendant`) —
    // the public-v2 `FolderResponse` doesn't expose the materialised `path` so we can't gate
    // it client-side without a roundtrip.
    return true;
  }

  private async performMove(payload: DocumentMgmtDragPayload, target: FolderResponse): Promise<void> {
    if (!target.id) return;
    try {
      if (payload.kind === 'document') {
        await this.documentsService.move(payload.id, target.id);
      } else {
        await this.foldersService.move(payload.id, target.id);
      }
      this.moveCompleted.emit({
        sourceParentId: payload.sourceParentId,
        targetParentId: target.id,
      });
      this.reload();
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.MOVE_FAILED',
      );
      this.uploadFailures.update((list) => [
        ...list,
        { id: `move-failed-${Date.now()}`, fileName: payload.name, message },
      ]);
    }
  }

  /**
   * Removes an incomplete document row (one whose finalize never succeeded — `current_version_id`
   * is null) by archiving + hard-deleting in one click. The button is the user's escape hatch
   * for "the upload failed and now there's a stuck row in the list".
   */
  protected async onRemoveIncomplete(doc: DocumentResponse): Promise<void> {
    if (!doc.id) return;
    try {
      await this.documentsService.hardRemove(doc.id);
      this.reload();
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.UPLOAD_FAILED',
      );
      this.uploadFailures.update((list) => [
        ...list,
        { id: `remove-failed-${Date.now()}`, fileName: doc.name ?? '', message },
      ]);
    }
  }

  /** True iff the document has never had a finalized version — i.e. finalize failed mid-upload. */
  protected isIncomplete(doc: DocumentResponse): boolean {
    return doc.currentVersionId == null;
  }

  protected onSelectSmart(kind: SmartListKind): void {
    if (this.busy()) return;
    this.smartSelected.emit(kind);
  }

  protected onSelectSmartKeydown(event: KeyboardEvent, kind: SmartListKind): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectSmart(kind);
    }
  }

  protected mimeColorClassFor(mime: string | undefined): string {
    return mimeColorClass(mime);
  }

  protected mimeShortLabel(mime: string | undefined): string {
    if (!mime) return '—';
    const slash = mime.indexOf('/');
    return slash >= 0 ? mime.slice(slash + 1).toUpperCase().slice(0, 4) : mime.slice(0, 4).toUpperCase();
  }

  protected formatSize(bytes: number | undefined): string {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  protected pendingUploadStatus(pending: PendingUpload): string {
    if (pending.phase === 'preparing') {
      return this.translate.instant('DOCUMENT_MANAGEMENT.PREPARING_ARCHIVE');
    }
    if (pending.phase === 'queued') {
      return this.translate.instant('DOCUMENT_MANAGEMENT.UPLOAD_QUEUED');
    }
    if (pending.phase === 'finalizing') {
      return this.translate.instant('DOCUMENT_MANAGEMENT.UPLOAD_FINALIZING');
    }
    if (pending.phase === 'complete') {
      return this.translate.instant('DOCUMENT_MANAGEMENT.UPLOAD_COMPLETE');
    }
    if (pending.progress === null) {
      return this.translate.instant('DOCUMENT_MANAGEMENT.UPLOADING');
    }
    return this.translate.instant('DOCUMENT_MANAGEMENT.UPLOAD_PROGRESS', {
      percent: pending.progress,
    });
  }

  protected pendingUploadProgressWidth(pending: PendingUpload): string {
    return `${this.pendingUploadProgressValue(pending)}%`;
  }

  protected pendingUploadProgressValue(pending: PendingUpload): number {
    if (pending.phase === 'preparing') return 0;
    if (pending.phase === 'finalizing' || pending.phase === 'complete') return 100;
    return pending.progress ?? 0;
  }

  /** Imperative reload, used by retry buttons. */
  reload(): void {
    this.load(this.parentId());
  }

  private async load(parentId: string | null): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);
    try {
      const documentSort = documentSortFor(this.sortKey());
      const [folderResponse, documentResponse] = await Promise.all([
        this.foldersService.listChildren(parentId, { size: 100 }),
        parentId == null
          ? Promise.resolve(null)
          : this.documentsService.listInFolder(parentId, { size: 100, sort: documentSort }),
      ]);
      // The async results may belong to a stale `parentId` if the user drilled in mid-flight;
      // bail out unless we're still on the same column scope.
      if (parentId !== this.parentId()) return;
      this.folders.set(sortFoldersManagedFirst(folderResponse.items ?? []));
      this.documents.set(documentResponse?.items ?? []);
    } catch {
      this.folders.set([]);
      this.documents.set([]);
      this.hasError.set(true);
    } finally {
      if (parentId === this.parentId()) this.isLoading.set(false);
    }
  }

  private async uploadFiles(files: File[]): Promise<void> {
    await this.uploadEntries(flatFileEntries(files));
  }

  private async uploadDroppedItems(dataTransfer: DataTransfer | null): Promise<void> {
    if (!dataTransfer) return;
    const fallbackFiles = this.snapshotFiles(dataTransfer);
    const hasUnreadableExternalFileDrop = this.hasUnreadableExternalFileDrop(dataTransfer);
    const result = await dragUploadEntries(dataTransfer, {
      onArchivePreparing: (event) => this.addPreparingArchivePendingRow(event.displayPath),
    });
    if (result && result.entries.length > 0) {
      if (!await this.confirmLargeFolderUploadIfNeeded(result.stats)) {
        this.clearPendingUploadsForEntries(result.entries);
        return;
      }
      await this.uploadEntries(result.entries);
      return;
    }
    const fallbackUploadFiles = this.surfaceUnreadablePackageFallbackFiles(fallbackFiles);
    if (fallbackFiles.length > 0) {
      if (fallbackUploadFiles.length > 0) {
        await this.uploadFiles(fallbackUploadFiles);
      }
      return;
    }
    if (hasUnreadableExternalFileDrop) {
      this.surfaceUnreadableExternalFileDrop();
    }
  }

  private async uploadEntries(entries: readonly UploadBatchEntry[]): Promise<void> {
    // `null` here means the column is the tenant root; only allowed for the OS-folder-create
    // root path (`uploadDroppedItemsAtRoot`), which has already filtered the entries to ensure
    // every file lives inside a directory segment. The non-root path keeps its existing guard.
    const folderId: string | null = this.parentId();
    const folderCache = new Map<string | null, FolderResponse[]>();
    const documentCache = new Map<string, DocumentResponse[]>();
    const acceptedNames = new Map<string, Set<string>>();
    const pendingIds = this.addPendingUploadRows(entries);

    // Run uploads sequentially so server-side ordering is predictable and we don't fire-and-flood
    // the filestore on big drops. Per-file failures are isolated.
    for (const entry of entries) {
      if (entry.kind === 'failure') {
        this.surfaceUploadEntryFailure(entry);
        continue;
      }
      if (entry.kind === 'directory') {
        try {
          await this.resolveFolderPath(folderId, entry.relativePath, folderCache);
        } catch (err) {
          const message = this.localFolderOrDocumentNameErrorMessage(err)
            ?? await translateProblem(
              err,
              this.translate,
              'DOCUMENT_MANAGEMENT.CREATE_FOLDER_ERROR',
            );
          this.uploadFailures.update((list) => [
            ...list,
            { id: `folder-failed-${Date.now()}`, fileName: entry.displayPath, message },
          ]);
        }
        continue;
      }

      try {
        const targetFolderId = await this.resolveFolderPath(folderId, entry.folderPath, folderCache);
        await this.uploadFileEntry(
          targetFolderId,
          entry,
          this.requirePendingUploadId(entry, pendingIds),
          documentCache,
          acceptedNames,
        );
      } catch (err) {
        this.removePendingUpload(pendingIds.get(entry));
        const message = this.localFolderOrDocumentNameErrorMessage(err)
          ?? await translateProblem(
            err,
            this.translate,
            'DOCUMENT_MANAGEMENT.UPLOAD_FAILED',
          );
        this.uploadFailures.update((list) => [
          ...list,
          { id: `upload-failed-${Date.now()}`, fileName: entry.displayPath, message },
        ]);
      }
    }
    // After the batch, refresh once to sweep ghost rows in favor of persisted documents.
    this.reload();
    this.schedulePendingUploadClear(Array.from(pendingIds.values()));
  }

  private async uploadFileEntry(
    folderId: string,
    entry: UploadFileEntry,
    pendingId: string,
    documentCache: Map<string, DocumentResponse[]>,
    acceptedNames: Map<string, Set<string>>,
  ): Promise<void> {
    let uploadName = requireValidFolderOrDocumentName(entry.uploadName);

    while (true) {
      const conflict = await this.findDocumentConflict(folderId, uploadName, documentCache, acceptedNames);
      if (!conflict) {
        try {
          const uploaded = await this.uploadOne(folderId, entry, uploadName, pendingId);
          this.addDocumentToCache(folderId, uploaded, documentCache);
          this.addAcceptedName(folderId, uploaded.name ?? uploadName, acceptedNames);
          return;
        } catch (err) {
          if (await this.isNameTakenError(err)) {
            await this.refreshDocumentCache(folderId, documentCache);
            this.updatePendingUpload(pendingId, {
              phase: 'queued',
              progress: 0,
            });
            continue;
          }
          throw err;
        }
      }

      const decision = await this.promptConflict(
        folderId,
        entry,
        uploadName,
        conflict,
        documentCache,
        acceptedNames,
      );
      if (decision.kind === 'skip') {
        this.removePendingUpload(pendingId);
        return;
      }
      if (decision.kind === 'rename') {
        uploadName = decision.name;
        this.updatePendingUpload(pendingId, {
          fileName: this.displayPathFor(entry, uploadName),
        });
        continue;
      }
      if (!conflict.document?.id) {
        this.removePendingUpload(pendingId);
        return;
      }
      await this.uploadNewVersion(conflict.document, entry, uploadName, pendingId);
      return;
    }
  }

  private async uploadOne(
    folderId: string,
    entry: UploadFileEntry,
    uploadName: string,
    pendingId: string,
  ): Promise<DocumentResponse> {
    const displayPath = this.displayPathFor(entry, uploadName);
    this.updatePendingUpload(pendingId, {
      fileName: displayPath,
      phase: 'uploading',
      progress: 0,
    });
    const intent = await this.documentsService.initiateUpload({
      name: uploadName,
      folderId,
      mimeType: entry.file.type || 'application/octet-stream',
    });
    if (!intent.documentId || !intent.upload?.url || !intent.upload.formFields) {
      throw new Error('Server did not return a complete upload intent');
    }
    await uploadBinary(
      intent.upload.url,
      intent.upload.formFields,
      entry.file,
      (progress) => this.onBinaryUploadProgress(pendingId, progress),
    );
    this.updatePendingUpload(pendingId, {
      phase: 'finalizing',
      progress: 100,
    });
    const uploaded = await this.documentsService.finalizeUpload(intent.documentId);
    this.updatePendingUpload(pendingId, {
      phase: 'complete',
      progress: 100,
    });
    return uploaded;
  }

  private async uploadNewVersion(
    document: DocumentResponse,
    entry: UploadFileEntry,
    uploadName: string,
    pendingId: string,
  ): Promise<void> {
    if (!document.id) {
      this.removePendingUpload(pendingId);
      return;
    }
    const displayPath = this.displayPathFor(entry, uploadName);
    this.updatePendingUpload(pendingId, {
      fileName: displayPath,
      phase: 'uploading',
      progress: 0,
    });
    try {
      const intent = await this.documentsService.initiateVersionUpload({
        documentId: document.id,
        mimeType: entry.file.type || 'application/octet-stream',
        originalFileName: uploadName,
      });
      if (!intent.documentId || !intent.upload?.url || !intent.upload.formFields) {
        throw new Error('Server did not return a complete upload intent');
      }
      await uploadBinary(
        intent.upload.url,
        intent.upload.formFields,
        entry.file,
        (progress) => this.onBinaryUploadProgress(pendingId, progress),
      );
      this.updatePendingUpload(pendingId, {
        phase: 'finalizing',
        progress: 100,
      });
      await this.documentsService.finalizeUpload(intent.documentId);
      this.updatePendingUpload(pendingId, {
        phase: 'complete',
        progress: 100,
      });
    } catch (err) {
      const message = await translateProblem(
        err,
        this.translate,
        'DOCUMENT_MANAGEMENT.UPLOAD_FAILED',
      );
      this.uploadFailures.update((list) => [
        ...list,
        { id: `${pendingId}-failed`, fileName: displayPath, message },
      ]);
      this.removePendingUpload(pendingId);
    }
  }

  private async confirmLargeFolderUploadIfNeeded(stats: UploadDropStats): Promise<boolean> {
    if (!shouldConfirmLargeFolderUpload(stats)) return true;
    return new Promise((resolve) => {
      let settled = false;
      const settle = (value: boolean) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      this.confirmationService.confirm({
        header: this.translate.instant('DOCUMENT_MANAGEMENT.LARGE_FOLDER_UPLOAD_TITLE'),
        message: this.translate.instant('DOCUMENT_MANAGEMENT.LARGE_FOLDER_UPLOAD_MESSAGE', {
          files: stats.fileCount,
          folders: stats.folderCount,
          size: this.formatSize(stats.totalBytes),
        }),
        acceptLabel: this.translate.instant('DOCUMENT_MANAGEMENT.LARGE_FOLDER_UPLOAD_ACCEPT'),
        rejectLabel: this.translate.instant('GENERAL.CANCEL'),
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => settle(true),
        reject: () => settle(false),
      });
    });
  }

  private statsForEntries(entries: readonly UploadBatchEntry[]): UploadDropStats {
    const stats = entries.reduce<UploadDropStats>(
      (acc, entry) => {
        if (entry.kind === 'directory') {
          return { ...acc, folderCount: acc.folderCount + 1 };
        }
        if (entry.kind === 'file') {
          return {
            ...acc,
            fileCount: acc.fileCount + 1,
            totalBytes: acc.totalBytes + entry.file.size,
          };
        }
        return acc;
      },
      { totalBytes: 0, fileCount: 0, folderCount: 0, requiresLargeFolderConfirmation: false },
    );
    return {
      ...stats,
      requiresLargeFolderConfirmation: shouldConfirmLargeFolderUpload(stats),
    };
  }

  private surfaceUploadEntryFailure(entry: UploadFailureEntry): void {
    this.removePendingUpload(entry.pendingUploadId);
    this.uploadFailures.update((list) => [
      ...list,
      {
        id: `upload-failed-${Date.now()}`,
        fileName: entry.displayPath,
        message: this.packageFailureMessage(entry),
      },
    ]);
  }

  private hasUnreadableExternalFileDrop(dataTransfer: DataTransfer): boolean {
    if (!dataTransferHasType(dataTransfer, 'Files')) return false;
    return Array.from(dataTransfer.items ?? []).some((item) => item.kind === 'file');
  }

  private snapshotFiles(dataTransfer: DataTransfer): File[] {
    return Array.from(dataTransfer.files ?? []);
  }

  private surfaceUnreadablePackageFallbackFiles(files: readonly File[]): File[] {
    const uploadFiles: File[] = [];
    for (const file of files) {
      if (isPackageDirectoryName(file.name)) {
        this.uploadFailures.update((list) => [
          ...list,
          {
            id: `package-unreadable-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            fileName: file.name,
            message: this.translate.instant('DOCUMENT_MANAGEMENT.PACKAGE_UPLOAD_UNREADABLE', {
              name: file.name,
            }),
          },
        ]);
        continue;
      }
      uploadFiles.push(file);
    }
    return uploadFiles;
  }

  private surfaceUnreadableExternalFileDrop(): void {
    this.uploadFailures.update((list) => [
      ...list,
      {
        id: `drop-unreadable-${Date.now()}`,
        fileName: '',
        message: this.translate.instant('DOCUMENT_MANAGEMENT.FOLDER_DROP_UNREADABLE'),
      },
    ]);
  }

  private packageFailureMessage(entry: UploadFailureEntry): string {
    const name = entry.displayPath;
    switch (entry.reason) {
      case 'package-too-large':
        return this.translate.instant('DOCUMENT_MANAGEMENT.PACKAGE_UPLOAD_TOO_LARGE', {
          name,
          size: this.formatSize(entry.sizeBytes),
        });
      case 'package-too-many-files':
        return this.translate.instant('DOCUMENT_MANAGEMENT.PACKAGE_UPLOAD_TOO_MANY_FILES', {
          name,
          count: entry.fileCount ?? 0,
        });
      case 'package-zip-failed':
        return this.translate.instant('DOCUMENT_MANAGEMENT.PACKAGE_UPLOAD_ZIP_FAILED', { name });
      case 'package-unreadable':
      default:
        return this.translate.instant('DOCUMENT_MANAGEMENT.PACKAGE_UPLOAD_UNREADABLE', { name });
    }
  }

  private addPendingUploadRows(
    entries: readonly UploadBatchEntry[],
  ): Map<UploadFileEntry, string> {
    const pendingIds = new Map<UploadFileEntry, string>();
    const pendingRows: PendingUpload[] = [];
    for (const entry of entries) {
      if (entry.kind !== 'file') continue;
      if (entry.pendingUploadId) {
        pendingIds.set(entry, entry.pendingUploadId);
        this.updatePendingUpload(entry.pendingUploadId, {
          fileName: entry.displayPath,
          mimeType: entry.file.type || 'application/octet-stream',
          phase: 'queued',
          progress: 0,
        });
        continue;
      }
      const id = `${entry.displayPath}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      pendingIds.set(entry, id);
      pendingRows.push({
        id,
        fileName: entry.displayPath,
        mimeType: entry.file.type || 'application/octet-stream',
        phase: 'queued',
        progress: 0,
      });
    }
    if (pendingRows.length > 0) {
      this.pendingUploads.update((list) => [...list, ...pendingRows]);
    }
    return pendingIds;
  }

  private addPreparingArchivePendingRow(displayPath: string): string {
    const id = `${displayPath}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.pendingUploads.update((list) => [
      ...list,
      {
        id,
        fileName: displayPath,
        mimeType: ZIP_MIME_TYPE,
        phase: 'preparing',
        progress: null,
      },
    ]);
    return id;
  }

  private clearPendingUploadsForEntries(entries: readonly UploadBatchEntry[]): void {
    for (const entry of entries) {
      if (entry.kind === 'file' || entry.kind === 'failure') {
        this.removePendingUpload(entry.pendingUploadId);
      }
    }
  }

  private requirePendingUploadId(
    entry: UploadFileEntry,
    pendingIds: Map<UploadFileEntry, string>,
  ): string {
    const id = pendingIds.get(entry);
    if (!id) throw new Error('Upload entry did not have a pending row');
    return id;
  }

  private onBinaryUploadProgress(pendingId: string, progress: BinaryUploadProgress): void {
    this.updatePendingUpload(pendingId, {
      phase: 'uploading',
      progress: progress.percent,
    });
  }

  private updatePendingUpload(
    id: string | undefined,
    patch: Partial<Omit<PendingUpload, 'id'>>,
  ): void {
    if (!id) return;
    this.pendingUploads.update((list) =>
      list.map((pending) =>
        pending.id === id
          ? { ...pending, ...patch }
          : pending,
      ),
    );
  }

  private removePendingUpload(id: string | undefined): void {
    if (!id) return;
    this.pendingUploads.update((list) => list.filter((pending) => pending.id !== id));
  }

  private schedulePendingUploadClear(ids: readonly string[]): void {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const timer = setTimeout(() => {
      this.pendingUploadClearTimers.delete(timer);
      this.pendingUploads.update((list) => list.filter((pending) => !idSet.has(pending.id)));
    }, 500);
    this.pendingUploadClearTimers.add(timer);
  }

  private async resolveFolderPath(
    rootFolderId: string | null,
    relativePath: string,
    folderCache: Map<string | null, FolderResponse[]>,
  ): Promise<string> {
    let currentParentId: string | null = rootFolderId;
    for (const segment of folderSegments(relativePath)) {
      currentParentId = await this.getOrCreateChildFolder(
        currentParentId,
        requireValidFolderOrDocumentName(segment),
        folderCache,
      );
    }
    if (currentParentId === null) {
      // Only reachable if the relative path is empty AND we're at the tenant root, but the
      // call sites always pass a non-empty `relativePath` for files and a stripped path for
      // directories, so this branch indicates a programming error rather than a runtime case.
      throw new Error('resolveFolderPath ended at the tenant root without a folder segment');
    }
    return currentParentId;
  }

  private async getOrCreateChildFolder(
    parentId: string | null,
    name: string,
    folderCache: Map<string | null, FolderResponse[]>,
  ): Promise<string> {
    const existing = (await this.childrenForParent(parentId, folderCache))
      .find((folder) => normalizeLookupName(folder.name ?? '') === normalizeLookupName(name));
    if (existing?.id) return existing.id;

    try {
      const created = await this.foldersService.create({ parentId: parentId ?? undefined, name });
      if (!created.id) throw new Error('Created folder did not include an id');
      folderCache.set(parentId, [...(folderCache.get(parentId) ?? []), created]);
      return created.id;
    } catch (err) {
      if (!(await this.isNameTakenError(err))) throw err;
      const children = await this.refreshFolderCache(parentId, folderCache);
      const raced = children.find((folder) =>
        normalizeLookupName(folder.name ?? '') === normalizeLookupName(name),
      );
      if (raced?.id) return raced.id;
      throw err;
    }
  }

  private async childrenForParent(
    parentId: string | null,
    folderCache: Map<string | null, FolderResponse[]>,
  ): Promise<FolderResponse[]> {
    const cached = folderCache.get(parentId);
    if (cached) return cached;
    return this.refreshFolderCache(parentId, folderCache);
  }

  private async refreshFolderCache(
    parentId: string | null,
    folderCache: Map<string | null, FolderResponse[]>,
  ): Promise<FolderResponse[]> {
    const folders = await this.listAllFolders(parentId);
    folderCache.set(parentId, folders);
    return folders;
  }

  private async listAllFolders(parentId: string | null): Promise<FolderResponse[]> {
    const items: FolderResponse[] = [];
    let page = 0;
    let totalPages = 1;
    while (page < totalPages) {
      const response = await this.foldersService.listChildren(parentId, { page, size: 100 });
      items.push(...(response.items ?? []));
      totalPages = response.page?.totalPages ?? 0;
      page += 1;
    }
    return items;
  }

  private async listAllDocumentsInFolder(folderId: string): Promise<DocumentResponse[]> {
    const items: DocumentResponse[] = [];
    let page = 0;
    let totalPages = 1;
    while (page < totalPages) {
      const response = await this.documentsService.listInFolder(folderId, { page, size: 100 });
      items.push(...(response.items ?? []));
      totalPages = response.page?.totalPages ?? 0;
      page += 1;
    }
    return items;
  }

  private async documentsForFolder(
    folderId: string,
    documentCache: Map<string, DocumentResponse[]>,
  ): Promise<DocumentResponse[]> {
    const cached = documentCache.get(folderId);
    if (cached) return cached;
    return this.refreshDocumentCache(folderId, documentCache);
  }

  private async refreshDocumentCache(
    folderId: string,
    documentCache: Map<string, DocumentResponse[]>,
  ): Promise<DocumentResponse[]> {
    const documents = await this.listAllDocumentsInFolder(folderId);
    documentCache.set(folderId, documents);
    return documents;
  }

  private async findDocumentConflict(
    folderId: string,
    uploadName: string,
    documentCache: Map<string, DocumentResponse[]>,
    acceptedNames: Map<string, Set<string>>,
  ): Promise<DocumentNameConflict | null> {
    const lookup = normalizeLookupName(uploadName);
    if (acceptedNames.get(folderId)?.has(lookup)) {
      return { name: uploadName, document: null };
    }
    const existing = (await this.documentsForFolder(folderId, documentCache))
      .find((doc) => normalizeLookupName(doc.name ?? '') === lookup);
    return existing ? { name: existing.name ?? uploadName, document: existing } : null;
  }

  private async promptConflict(
    folderId: string,
    entry: UploadFileEntry,
    uploadName: string,
    conflict: DocumentNameConflict,
    documentCache: Map<string, DocumentResponse[]>,
    acceptedNames: Map<string, Set<string>>,
  ): Promise<ConflictDecision> {
    const unavailableNames = await this.unavailableDocumentNames(folderId, documentCache, acceptedNames);
    const suggestedName = nextAvailableFileName(uploadName, unavailableNames);
    const fileMime = entry.file.type || 'application/octet-stream';
    const existingMime = conflict.document?.mimeType;
    const canUploadNewVersion = Boolean(
      conflict.document?.id
      && conflict.document?.currentVersionId
      && existingMime
      && existingMime === fileMime,
    );
    const newVersionDisabledReason = canUploadNewVersion
      ? null
      : this.translate.instant('DOCUMENT_MANAGEMENT.CONFLICT_NEW_VERSION_DISABLED');

    return new Promise<ConflictDecision>((resolve) => {
      this.conflictDialogResolve = resolve;
      this.conflictRenameValue.set(suggestedName);
      this.conflictRenameError.set(null);
      this.conflictDialog.set({
        id: `${entry.displayPath}-${Date.now()}`,
        displayPath: entry.displayPath,
        uploadName,
        existingName: conflict.name,
        canUploadNewVersion,
        newVersionDisabledReason,
      });
    });
  }

  private async unavailableDocumentNames(
    folderId: string,
    documentCache: Map<string, DocumentResponse[]>,
    acceptedNames: Map<string, Set<string>>,
  ): Promise<ReadonlySet<string>> {
    const names = new Set<string>(acceptedNames.get(folderId) ?? []);
    for (const document of await this.documentsForFolder(folderId, documentCache)) {
      if (document.name) names.add(normalizeLookupName(document.name));
    }
    return names;
  }

  private addDocumentToCache(
    folderId: string,
    document: DocumentResponse,
    documentCache: Map<string, DocumentResponse[]>,
  ): void {
    const docs = documentCache.get(folderId);
    if (!docs) return;
    const documentId = document.id;
    const withoutExisting = documentId
      ? docs.filter((doc) => doc.id !== documentId)
      : docs;
    documentCache.set(folderId, [...withoutExisting, document]);
  }

  private addAcceptedName(
    folderId: string,
    name: string,
    acceptedNames: Map<string, Set<string>>,
  ): void {
    const names = acceptedNames.get(folderId) ?? new Set<string>();
    names.add(normalizeLookupName(name));
    acceptedNames.set(folderId, names);
  }

  private displayPathFor(entry: UploadFileEntry, uploadName: string): string {
    return entry.folderPath ? `${entry.folderPath}/${uploadName}` : uploadName;
  }

  private localFolderOrDocumentNameErrorMessage(err: unknown): string | null {
    if (err instanceof FolderOrDocumentNameValidationError) {
      return this.translate.instant(folderOrDocumentNameErrorKey(err.reason));
    }
    return null;
  }

  private didDragLeaveCurrentTarget(event: DragEvent): boolean {
    const currentTarget = event.currentTarget;
    if (!(currentTarget instanceof Element)) return true;
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node) return !currentTarget.contains(nextTarget);
    const bounds = currentTarget.getBoundingClientRect();
    return event.clientX < bounds.left
      || event.clientX >= bounds.right
      || event.clientY < bounds.top
      || event.clientY >= bounds.bottom;
  }

  private resolveConflictDialog(decision: ConflictDecision): void {
    const resolve = this.conflictDialogResolve;
    this.conflictDialogResolve = null;
    this.conflictDialog.set(null);
    this.conflictRenameValue.set('');
    this.conflictRenameError.set(null);
    resolve?.(decision);
  }

  private async isNameTakenError(err: unknown): Promise<boolean> {
    const problem = await parseProblemFromError(err);
    return problem?.status === 409 && problem.type?.includes('document-management/name-taken') === true;
  }
}

/**
 * Maps a UI sort preset to the documents endpoint's `sort=` query strings. Folders always render
 * sorted by name (managed-first lifting happens after the fetch in `sortFoldersManagedFirst`), so
 * only document presets need a translation.
 *
 * `uploaded-desc/asc` use `updatedAt` because finalize bumps it — close enough to "newest upload"
 * for the current schema. The backend whitelist (`name`, `createdAt`, `updatedAt`, `size`) silently
 * drops anything not on the list, so callers won't crash on a stale preference.
 */
function documentSortFor(sortKey: FolderSortKey): string[] {
  switch (sortKey) {
    case 'name-asc':
      return ['name,asc'];
    case 'name-desc':
      return ['name,desc'];
    case 'uploaded-desc':
      return ['updatedAt,desc'];
    case 'uploaded-asc':
      return ['updatedAt,asc'];
    case 'size-desc':
      return ['size,desc'];
    case 'size-asc':
      return ['size,asc'];
  }
}

/**
 * Returns a copy of the folder list with managed (`managed`, `managed_branch`) entries first and
 * user entries after, while preserving the server's order within each group. The server already
 * sorts by name; we just lift the system buckets to the top so the user sees them grouped — they
 * usually don't need to interact with these and treating them as visually distinct keeps the
 * top-of-list real estate for content the user actually owns.
 */
function sortFoldersManagedFirst(folders: readonly FolderResponse[]): FolderResponse[] {
  const managed: FolderResponse[] = [];
  const user: FolderResponse[] = [];
  for (const folder of folders) {
    if (folder.kind === 'managed' || folder.kind === 'managed_branch') {
      managed.push(folder);
    } else {
      user.push(folder);
    }
  }
  return [...managed, ...user];
}
